import { Box, Button, Typography } from "@mui/material";
import dayjs from "dayjs";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import { currencyFormatter } from "../../../../../utils/Formatter";
import { useState } from "react";
import EditInsurance from "./edit-insurance/edit-insurance";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { downloadTemporaryFileRequest } from "../../../../../utils/APICalls";
import Loading from "../../../../../components/loading/loading";
import ErrorNotification from "../../../../../components/error-notification/error-notification";

type Props = {
  data: any;
};

const InsuranceDetailRow = ({ data }: Props) => {
  const [showEditInsurance, setShowEditInsurance] = useState(false);
  const [addPolicyCase, setAddPolicyCase] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const onDownloadClick = async () => {
    if (!data?.policyDocumentKey) {
      return;
    }
    const filename = data?.policyDocumentName || "policy-document";
    setShowLoader(true);
    try {
      const response = await downloadTemporaryFileRequest(
        data.policyDocumentKey
      );
      if (response && response.status === 200) {
        let fileUrl = response.data;

        if (typeof fileUrl === "string") {
          fileUrl = fileUrl.trim();
          if (fileUrl.startsWith('"') && fileUrl.endsWith('"')) {
            fileUrl = JSON.parse(fileUrl);
          }
        }

        const a = document.createElement("a");
        a.href = fileUrl;
        a.download = filename;
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else if (response && response.status >= 400) {
        setShowErrorNotification(true);
        setErrorMessage("Failed to download document. Please try again.");
      }
    } catch (error) {
      setShowErrorNotification(true);
      setErrorMessage("Failed to download document. Please try again.");
    } finally {
      setShowLoader(false);
    }
  };

  return (
    <>
      {data?.insurerName ? (
        <Box sx={{ display: "flex" }}>
          <Box>
            <Typography className="label-header-text">Insurer</Typography>
            <Typography>{data?.insurerName}</Typography>
          </Box>
          <Box>
            <Typography className="label-header-text">
              Policy Amount*
            </Typography>
            <Typography>
              {data?.policyAmount
                ? `$${currencyFormatter(data?.policyAmount)}`
                : "-"}
            </Typography>
          </Box>
          <Box>
            <Typography className="label-header-text">
              Expiration Date*
            </Typography>
            <Typography>
              {data?.expiryDate
                ? dayjs(data?.expiryDate).format("MM/DD/YYYY")
                : " "}
            </Typography>
          </Box>
          <Box>
            <Typography className="label-header-text">
              Copy of Policy*
            </Typography>
            <Typography>
              {data?.policyDocumentKey && data?.policyDocumentName !== "" ? (
                <CheckCircleOutlineIcon color="success" />
              ) : (
                <HighlightOffIcon color="error" />
              )}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {data?.policyDocumentKey ? (
              <DownloadOutlinedIcon
                color="primary"
                sx={{ cursor: "pointer" }}
                onClick={onDownloadClick}
              />
            ) : (
              <Box sx={{ width: 24 }} />
            )}
            <EditOutlinedIcon
              sx={{ cursor: "pointer", ml: 2 }}
              color="primary"
              onClick={() => {
                setShowEditInsurance(true);
              }}
            />
          </Box>
        </Box>
      ) : (
        <Button
          variant="outlined"
          startIcon={<AddOutlinedIcon />}
          onClick={() => {
            setShowEditInsurance(true);
            setAddPolicyCase(true);
          }}
        >
          Add Policy
        </Button>
      )}
      {showEditInsurance && (
        <EditInsurance
          showDialog={showEditInsurance}
          closeDialog={() => {
            setAddPolicyCase(false);
            setShowEditInsurance(false);
          }}
          data={data}
          addPolicyCase={addPolicyCase}
        />
      )}
      {showLoader && <Loading />}
      <ErrorNotification
        show={showErrorNotification}
        message={errorMessage}
        onClose={() => setShowErrorNotification(false)}
      />
    </>
  );
};

export default InsuranceDetailRow;
